import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MasterDataService } from '../../../services/master-data.service';
import { Mode } from '../../../models/mode.model';
import { MaintenanceOrderStatus, MaintenanceOrderStatusCode } from '../../../models/maintenance-order-status.model';
import { Shop } from '../../../models/shop.model';
import { ReplaySubject, firstValueFrom, forkJoin, take, takeUntil } from 'rxjs';

import { RouterModule } from '@angular/router';

import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-input-date';
import { WorkOrderFilterForm } from '../../../form-model/work-order-filter.form.model';
import { User } from '../../../models/user.model';
import { AuthPolicies } from '../../../auth/auth-policies';
import { utils } from '../../../helper/utils';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
import { ShopManagementFilterForm } from '../../../form-model/shop-management-filter.form.model';
import { MasterDataManagementService } from '../../../services/master-data-management.service';
import { RegexConstants } from '../../../RegexConstants';
import { CodeDescription } from '../../../models/code-description.model';
import { getPartSupplierTypeDisplayText, PartSupplierType } from '../../../form-model/part-supplier-type.model';
import '@maersk-global/mds-components-core-typeahead-multi-select';

@Component({
  selector: 'app-shop-management-advanced-filter',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, AemsMultilineTagInputComponent],
  templateUrl: './shop-management-advanced-filter.component.html',
  styleUrl: './shop-management-advanced-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ShopManagementAdvancedFilterComponent implements OnInit {
  @Input({ required: true }) shopManagementFilterForm!: FormGroup<ShopManagementFilterForm>;
  @Input() isShopManagementList: boolean = true;
  @Input() bindMultiSelects: boolean = false;

  //modes: Mode[] = [];
  //statuses: MaintenanceOrderStatus[] = [];
  shops: Shop[] = [];
  shopCodeRegex = RegexConstants.SHOP_CODE;
  shopCodesList: string[] = [];
  partSupplierTypeList: PartSupplierType[] = [];
  locationCodesList: { label: string; value: string }[] = [];
  selectedLocations: { label: string; value: string }[] = [];
  activeStatusList = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ];

  isLoadingOptions: boolean = false;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private masterDataService: MasterDataService,
    private formBuilder: FormBuilder,
    public authPolicy: AuthPolicies,
    private masterDataManagementService: MasterDataManagementService,
  ) {}

  ngOnInit() {
    this.addControlsToTheFormGroup();
    this.loadUniqueFilterOptions();
    this.shopManagementFilterForm
      .get('locationCode')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((values: string[] | null) => {
        this.selectedLocations = (values ?? []).map((v) => ({ label: v, value: v }));
      });
  }

  private addControlsToTheFormGroup() {
    this.shopManagementFilterForm.addControl('shopCode', this.formBuilder.control<string[]>([]));
    this.shopManagementFilterForm.addControl('partSupplierType', this.formBuilder.control<string[]>([]));
    this.shopManagementFilterForm.addControl('locationCode', this.formBuilder.control<string[]>([]));
    this.shopManagementFilterForm.addControl('isActive', this.formBuilder.control<boolean[]>([]));
    this.shopManagementFilterForm.addControl('applyShopManagementFilter', this.formBuilder.control<boolean | null>(null));
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  async loadUniqueFilterOptions() {
    this.isLoadingOptions = true;
    try {
      const [locations] = await Promise.all([firstValueFrom(this.masterDataManagementService.getLocationCodes())]);
      this.locationCodesList = [...locations];
      this.shopCodesList = [];

      this.partSupplierTypeList = [
        {
          code: 'MANUFACTURER_PARTS_WITH_DISCOUNT',
          description: getPartSupplierTypeDisplayText('MANUFACTURER_PARTS_WITH_DISCOUNT'),
        },
        {
          code: 'MAERSK_SUPPLIED_PARTS',
          description: getPartSupplierTypeDisplayText('MAERSK_SUPPLIED_PARTS'),
        },
        {
          code: 'MANUFACTURER_PARTS',
          description: getPartSupplierTypeDisplayText('MANUFACTURER_PARTS'),
        },
        {
          code: 'SHOP_AND_MAERSK_SUPPLIED_PARTS',
          description: getPartSupplierTypeDisplayText('SHOP_AND_MAERSK_SUPPLIED_PARTS'),
        },
      ];
    } catch (err) {
      console.error('Error loading filter options', err);
    } finally {
      this.isLoadingOptions = false;
    }
  }

  isValueSelected(controlName: 'isActive', value: boolean): boolean {
    const arr = this.shopManagementFilterForm.get(controlName)?.value || [];
    return arr.includes(value);
  }

  onCheckChange(controlName: 'isActive', value: boolean): void {
    const control = this.shopManagementFilterForm.get(controlName);
    const current = control?.value ?? [];

    if (current.includes(value)) {
      control?.setValue(current.filter((v: boolean) => v !== value));
    } else {
      control?.setValue([...current, value]);
    }
  }
  onLocationSelected(event: any): void {
    const selectedItems = event.detail?.value ?? event.detail ?? [];
    this.selectedLocations = selectedItems.map((s: any) => ({ label: s.value ?? s, value: s.value ?? s }));
    this.shopManagementFilterForm.get('locationCode')?.setValue(this.selectedLocations.map((x) => x.value));
  }

  onLocationCleared(): void {
    this.selectedLocations = [];
    this.shopManagementFilterForm.get('locationCode')?.setValue([]);
  }

  applyFilters() {
    this.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
  }

  clearFilters() {
    this.shopManagementFilterForm.reset();
  }
}
